import { useMemo, useState } from "react";

// The brostar form handles the initial values so we dont have to do that.
// Only if there is a task or if its saved in the local storage should we set it
export function useBrostarFormDataStorage<T extends object>({
  key,
  task,
  transformData,
}: {
  key: string;
  task: T | undefined;
  transformData?: (data: T | undefined) => T | undefined;
}) {
  const storageIsDisabled = !!task;
  const [storedValue, setStoredValue] = useState<T | undefined>((): T | undefined => {
    if (storageIsDisabled) return task;

    const item = window.localStorage.getItem(key);

    if (item) return JSON.parse(item) as T;

    return undefined;
  });

  // Return a wrapped version of useState's setter function that
  // persists the new value to the Storage.
  const setValue = useMemo(
    () => (value: T | undefined) => {
      setStoredValue(value);

      if (!storageIsDisabled) {
        if (value === undefined) {
          window.localStorage.removeItem(key);
        } else {
          window.localStorage.setItem(key, JSON.stringify(value));
        }
      }
    },
    [key, storageIsDisabled]
  );

  const handleChange = ({ formData }: { formData?: T }) => {
    if (!formData) return setValue(task);
    setValue(transformData ? transformData(formData) : formData);
  };

  return { formData: storedValue, handleChange };
}
